import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Button from "../../../shared/Button";
import Modal from "../../../shared/Modal";
import useForm from "../../../hooks/useForm";
import { useNavigate } from "react-router-dom";
import AdvancedSettings from "./AdvancedSettings";
import { useAddJobMutation } from "../../../redux/services/jobs/jobsApi";
import { ServiceJobForm } from "./ServiceJobForm";
import TruckingJobForm from "./TruckingJobForm";
import { ClientsDropdown } from "../../../components/ClientsDropdown";
import {
  useAddClientMutation,
  useGetClientsQuery,
  useGetRecentClientsQuery,
  useLazyGetClientQuery,
} from "../../../redux/services/clients/clientsApi";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import { useAuth } from "../../../hooks/useAuth";
import { AvatarClient } from "../../../components";
import { renderValidations } from "@/shared/Table/table.utils";
import FileProcessor from "../../../components/FileProcessor";
import { FileViewer } from "../../../shared/FileViewer";
import {
  PRESET_CONFIGS,
  createExtractionConfig,
  selectBestDataForForm,
  selectBestDataForFormWithLocationResolution,
  ENHANCED_PRESET_CONFIGS,
} from "../../../components/FileProcessor/configs";
import { DocumentArrowUpIcon, PlusIcon } from "@heroicons/react/24/outline";
import LegCreate from "../Legs/LegCreate";
import TruckingWorkFlow from "./TruckingWorkFlow";
import ServiceJobFlow from "./ServiceJobFlow";
import { getProductTerminology } from "../../../constants/product-terminology";

const validations = renderValidations([{ id: "client", required: true }]);

const CreateModal = ({
  client,
  modalOpen,
  setModalOpen,
  showFullForm,
  onSuccess,
  disableNavigation,
}) => {
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const { user } = useAuth();
  const navigate = useNavigate();

  const { form, updateForm, clearForm, setFormInit, errors, isValid } = useForm(
    { validations }
  );

  const [slideInView, setSlideInView] = useState(0);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [parsedFileData, setParsedFileData] = useState(null);
  const [previewSheets, setPreviewSheets] = useState(null);
  const [fileUploadStep, setFileUploadStep] = useState("upload"); // "upload", "form"
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataSelectionMetadata, setDataSelectionMetadata] = useState(null);
  const [editableLegs, setEditableLegs] = useState([]);

  const [forceRenderCounter, setForceRenderCounter] = useState(0);
  const [submitButton, setSubmitButton] = useState("");

  const hasTriggeredRerenderRef = useRef(false);

  // Carousel hook with debug logging
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    watchDrag: false,
  });

  const [addJob, { isLoading: addingJob }] = useAddJobMutation();
  const [addClient, { isLoading: addingClient }] = useAddClientMutation();
  const { data: clientsData } = useGetClientsQuery({ limit: 30 });
  const { data: recentClientsData } = useGetRecentClientsQuery();
  const [triggerGetClient] = useLazyGetClientQuery();

  const clients = clientsData?.clients || [];

  const recentClients = useMemo(
    () => recentClientsData?.data || [],
    [recentClientsData]
  );
  const isCreatingNewClient = Boolean(form?.newClient && !form?.client?._id);
  const canCreateClient = Boolean(form?.client?.name?.trim());

  const getButtonTitle = useCallback(() => {
    return appType === "trucking" ? "Submit" : "Next";
  }, [appType]);

  useEffect(() => {
    setSubmitButton(getButtonTitle());
  }, [getButtonTitle]);

  // Set the client in the form when client data is provided directly
  useEffect(() => {
    // Only pre-set the client when a real client is provided (has an `_id`).
    // This prevents callers from passing an empty object (or a new object reference
    // each render) which would overwrite the user's selection and look like it
    // "reverts to no client".
    if (client?._id && modalOpen && !form.client?._id) {
      updateForm({ id: "client", value: client });

      // Auto-advance to next slide for service repair jobs when client is preset
      if (appType !== "trucking" && emblaApi && emblaApi.canScrollNext()) {
        setSubmitButton("Submit");
        setSlideInView(1);
        emblaApi.scrollNext();
      }
    }
  }, [client?._id, modalOpen, form.client?._id, updateForm, emblaApi, appType]);

  useEffect(() => {
    if (form.client) {
      setFormInit({ appointmentDate: null });
      if (appType === "trucking") {
        setEditableLegs([]);
      }
    }
  }, [form.client]);

  function closeModal() {
    setModalOpen(false);
    handleCancel();
  }

  async function saveJob() {
    console.log("SAVE JOB", editableLegs);

    try {
      const postData = {
        ...form,
        useLegacyCreate: false,
      };

      // Add jobId with the same value as loadNumber
      if (form.loadNumber) {
        postData.jobId = form.loadNumber;
      }

      // Include legs data for both file upload and manual creation
      if (parsedFileData) {
        // File upload flow - use extractedData format

        const calculatedLegs =
          editableLegs?.filter((leg) => leg.totalMiles && leg.baseCharge)
            .length || 0;

        postData.extractedData = {
          legs: editableLegs, // Use the edited legs for file upload
          sheets: previewSheets || undefined,
          fileName: selectedFile?.name,
        };
      } else if (editableLegs && editableLegs.length > 0) {
        // Manual creation flow - use extractedData format to match backend processing

        // For manual creation
        postData.extractedData = {
          legs: editableLegs, // Use the edited legs for file upload
        };
      } else {
        // Debug: No legs found
        // console.log(`⚠️ No legs data found for submission:`, {
        //   parsedFileData: !!parsedFileData,
        //   editableLegsLength: editableLegs?.length || 0,
        //   editableLegs: editableLegs,
        //   formData: Object.keys(form),
        // });
      }

      if (form.template) {
        postData.template = form.template.value;
      }

      const res = await addJob(postData).unwrap();

      if (res.status !== "success") {
        toast(res.message, {
          toastId: "add-job-error",
          type: res.status,
        });
        return;
      } else {
        if (typeof onSuccess === "function") {
          onSuccess(res.data);
        }
        closeModal();
        if (!disableNavigation) {
          navigate("/jobs/" + res.data._id);
        }
      }
    } catch (error) {
      console.error("Error saving job:", error);
      toast("Error saving job", {
        toastId: "add-job-error",
        type: "error",
      });
    }
  }

  const handleCancel = useCallback(() => {
    setSubmitButton(getButtonTitle());
    setSlideInView(0);
    setShowFileUpload(false);
    setParsedFileData(null);
    setEditableLegs([]);
    setFileUploadStep("upload");
    clearForm(["client", "newClient"]);
    if (emblaApi) {
      emblaApi.scrollTo(0);
    }
  }, [emblaApi, getButtonTitle]);

  // Handle carousel back with debug
  const scrollPrev = useCallback(() => {
    if (emblaApi && emblaApi.canScrollPrev()) {
      setSubmitButton(getButtonTitle());
      setSlideInView(slideInView - 1);
      emblaApi.scrollPrev();
    }
  }, [emblaApi, slideInView]);

  // Handle carousel next with debug
  const scrollNext = useCallback(() => {
    if (emblaApi && emblaApi.canScrollNext()) {
      setSubmitButton("Submit");
      setSlideInView(slideInView + 1);
      emblaApi.scrollNext();
    } else {
    }
  }, [emblaApi, slideInView]);

  /**
   * Handles the selection of a client and scrolls to the next view.
   *
   * @param {Object} cl - The client object selected by the user.
   */
  const handleSelectClient = useCallback(
    (cl) => {
      updateForm({ id: "client", value: cl });

      if (appType !== "trucking") {
        scrollNext();
      }
    },
    [scrollNext, updateForm, appType]
  );

  async function addClientAndContinue({ scrollAfterCreate = false } = {}) {
    try {
      if (!form.client || !form.client.name?.trim()) {
        toast("Please fill out the client form before proceeding.", {
          toastId: "client-form-error",
          type: "error",
        });
        return;
      }

      const clientRes = await addClient(form.client).unwrap();

      toast(clientRes.message, {
        toastId: `add-client-${clientRes.status}`,
        type: clientRes.status,
      });

      if (clientRes.status !== "success") {
        return;
      }

      updateForm({ id: "client", value: clientRes.data });
      updateForm({ id: "newClient", value: false });

      if (scrollAfterCreate && appType !== "trucking") {
        scrollNext();
      }
    } catch (error) {
      toast("Error adding client", {
        toastId: "add-client-error",
        type: "error",
      });
    }
  }

  // Main modal button handler with debug
  function handleSubmit() {
    if (isCreatingNewClient) {
      toast("Create the client first, then continue with job details.", {
        toastId: "create-client-first",
        type: "warning",
      });
      return;
    }

    // Handle file upload flow for trucking
    if (appType === "trucking" && showFileUpload) {
      if (fileUploadStep === "upload") {
        // File upload step - wait for file to be processed
        return;
      } else if (fileUploadStep === "form") {
        // Form step - save the job
        saveJob();
        return;
      }
    }

    // Original logic for all other cases
    if (appType !== "trucking") {
      // If we're on the first slide (client selection)
      if (slideInView === 0) {
        // If we have a client selected, just scroll next
        if (form.client?._id) {
          scrollNext();
          return;
        }
      }

      // Only save job if we're on the last slide
      if (slideInView === 1) {
        saveJob();
      }
    } else {
      saveJob();
    }
  }

  // Handle file data parsing
  const handleFileDataParsed = useCallback(
    async (extractedData, rawData) => {
      // Reset re-render trigger for new file upload
      hasTriggeredRerenderRef.current = false;

      // Use intelligent data selection for multi-sheet files
      const configType =
        appType === "trucking" ? "TRUCKING_LOAD" : "SERVICE_JOBS";

      let result;

      // Try to use enhanced extraction with location resolution if we have the required parameters
      if (user?.parentCompany && form.client?._id) {
        try {
          result = await selectBestDataForFormWithLocationResolution(
            extractedData,
            configType,
            user.parentCompany,
            form.client._id
          );

          if (result.metadata?.locationsPreResolved) {
            toast.success(
              `Successfully imported data with ${
                result.metadata.locationsResolvedCount || 0
              } locations pre-resolved!`,
              { duration: 4000 }
            );
          }
        } catch (error) {
          console.error(
            "⚠️ Enhanced extraction failed, falling back to basic extraction:",
            error
          );
          result = selectBestDataForForm(extractedData, configType);
          toast.warning(
            "Location pre-resolution failed, but data was imported successfully"
          );
        }
      } else {
        // Fallback to basic extraction
        result = selectBestDataForForm(extractedData, configType);
      }

      // 🏢 PRE-FETCH CLIENT ORIGIN LOCATION for route calculations
      if (form.client?._id) {
        try {
          const clientData = await triggerGetClient({
            id: form.client._id,
          }).unwrap();

          if (clientData?.locations?.length > 0) {
            const clientOriginLocation = clientData.locations[0];

            // Store client origin location in the form for TruckingJobForm to use
            // updateForm({
            //   id: "clientOriginLocation",
            //   value: clientOriginLocation,
            // });
          } else {
            // Try client's primary address as fallback
            if (clientData?.address) {
              const fallbackLocation = {
                _id: `temp-client-${form.client._id}`,
                name: clientData.name || form.client.name || "Client Location",
                address: {
                  line1: clientData.address.line1 || "",
                  line2: clientData.address.line2 || "",
                  city: clientData.address.city || "",
                  state: clientData.address.state || "",
                  zip: clientData.address.zip || "",
                  fullAddress: `${clientData.address.line1 || ""} ${
                    clientData.address.city || ""
                  } ${clientData.address.state || ""} ${
                    clientData.address.zip || ""
                  }`.trim(),
                },
              };

              // updateForm({
              //   id: "clientOriginLocation",
              //   value: fallbackLocation,
              // });
            }
          }
        } catch (error) {
          console.error("⚠️ Error pre-fetching client location:", error);
        }
      }

      const { selectedData, legs, metadata } = result;

      // Store the complete parsed data including legs
      setParsedFileData({
        selectedData,
        legs,
        metadata,
        rawData: extractedData,
      });

      // Store editable legs separately for form editing

      setEditableLegs(legs || []);

      // Store metadata for UI display
      setDataSelectionMetadata(metadata);

      // Define job-level fields that should be in the main form
      const jobLevelFields = new Set([
        "loadNumber",
        "transactionDate",
        "baseRate",
        "fuelRate",
        "flatRate",
        "useFlatRate",
        "appointmentDate",
        "note",
        "useGlobalRates",
      ]);

      // Auto-populate form with intelligently selected data (job-level only)
      if (Object.keys(selectedData).length > 0) {
        // Update form with selected data, but only job-level fields
        Object.entries(selectedData).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            jobLevelFields.has(key)
          ) {
            updateForm({ id: key, value });
          }
        });

        // Show success message with legs info (only if not already shown for location resolution)
        if (!metadata?.locationsPreResolved) {
          const legsMessage =
            legs && legs.length > 0 ? ` and ${legs.length} delivery legs` : "";
          toast.success(`Successfully imported form data${legsMessage}`, {
            duration: 3000,
          });
        }
      } else {
        // Fallback to original logic if intelligent selection fails
        if (extractedData.length > 0) {
          const firstRow = extractedData[0];

          // Update form with extracted data, but only job-level fields
          Object.entries(firstRow).forEach(([key, value]) => {
            if (
              value !== undefined &&
              value !== null &&
              value !== "" &&
              jobLevelFields.has(key)
            ) {
              // updateForm({ id: key, value });
            }
          });

          toast.warning(
            `Used first available record from file (${extractedData.length} total records found)`
          );
        } else {
          toast.error("No usable data found in the uploaded file");
        }
      }

      setFileUploadStep("form");
    },
    [updateForm, appType, user?.parentCompany, form.client?._id]
  );

  // Handle file parsing errors
  const handleFileError = useCallback((error) => {
    console.error("File parsing error:", error);
    toast.error(`Error parsing file: ${error.message}`);
  }, []);

  // Get parser configuration based on app type
  const getParserConfig = useCallback(() => {
    if (appType === "trucking") {
      return ENHANCED_PRESET_CONFIGS.TRUCKING_LOAD;
    } else {
      return ENHANCED_PRESET_CONFIGS.SERVICE_JOBS;
    }
  }, [appType]);

  // Handle direct file selection
  const handleDirectFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewSheets(null);
    setShowFileUpload(true);
    setFileUploadStep("upload");

    // Reset the file input
    event.target.value = "";
  }, []);

  // Trigger file input click
  const triggerFileInput = useCallback(() => {
    document.getElementById("hidden-file-input").click();
  }, []);

  const footer = (
    <div>
      <Button className="mr-3" onClick={closeModal} type="alternative">
        Cancel
      </Button>
      {/* Show back button for service jobs on second slide or trucking file upload mode */}
      {((appType !== "trucking" && submitButton === "Submit") ||
        (appType === "trucking" &&
          showFileUpload &&
          fileUploadStep === "form")) && (
        <Button
          type="alternative"
          className="mr-2"
          onClick={
            appType === "trucking" && showFileUpload
              ? () => {
                  setShowFileUpload(false);
                  setFileUploadStep("upload");
                  setParsedFileData(null);
                  setEditableLegs([]);
                  setDataSelectionMetadata(null);
                }
              : scrollPrev
          }
        >
          Back
        </Button>
      )}
      {isCreatingNewClient && (
        <Button
          type="alternative"
          className="mr-2"
          loading={addingClient}
          disabled={addingClient || !canCreateClient}
          onClick={() =>
            addClientAndContinue({
              scrollAfterCreate: appType !== "trucking" && slideInView === 0,
            })
          }
        >
          Create Client
        </Button>
      )}
      <Button
        loading={addingJob}
        disabled={addingJob || addingClient || !isValid || isCreatingNewClient}
        onClick={handleSubmit}
        type="primary"
      >
        {appType === "trucking" && showFileUpload && fileUploadStep === "form"
          ? terms.createJobFromFileTitle
          : submitButton}
      </Button>
    </div>
  );

  const mainContent = useMemo(() => {
    // Handle trucking jobs (single step with file upload option)
    if (appType === "trucking") {
      // Show file upload interface if file upload is active
      return (
        <TruckingWorkFlow
          legs={editableLegs}
          setLegs={setEditableLegs}
          showFileUpload={showFileUpload}
          fileUploadStep={fileUploadStep}
          selectedFile={selectedFile}
          getParserConfig={getParserConfig}
          appType={appType}
          handleFileDataParsed={handleFileDataParsed}
          handleFileError={handleFileError}
          setShowFileUpload={setShowFileUpload}
          setFileUploadStep={setFileUploadStep}
          setParsedFileData={setParsedFileData}
          setDataSelectionMetadata={setDataSelectionMetadata}
          form={form}
          updateForm={updateForm}
          triggerFileInput={triggerFileInput}
          handleDirectFileSelect={handleDirectFileSelect}
          errors={errors}
          forceRenderCounter={forceRenderCounter}
          recentClients={recentClients}
          handleSelectClient={handleSelectClient}
          handleCancel={closeModal}
          showFullForm={showFullForm}
          parsedFileData={parsedFileData}
          onSheetsParsed={setPreviewSheets}
        />
      );
    }

    // Service jobs - existing carousel structure
    return (
      <ServiceJobFlow
        emblaRef={emblaRef}
        slideInView={slideInView}
        form={form}
        updateForm={updateForm}
        handleCancel={closeModal}
        handleSelectClient={handleSelectClient}
        errors={errors}
        recentClients={recentClients}
      />
    );
  }, [
    slideInView,
    form,
    clients.length,
    errors,
    emblaApi,
    appType,
    showFileUpload,
    fileUploadStep,
    parsedFileData,
    recentClients,
    forceRenderCounter,
  ]);

  const title = (
    <div className="flex items-center justify-between w-full mx-4">
      <div className="flex items-center space-x-3">
        {appType === "trucking" && showFileUpload && (
          <button
            onClick={() => {
              setShowFileUpload(false);
              setFileUploadStep("upload");
              setParsedFileData(null);
              setEditableLegs([]);
              setDataSelectionMetadata(null);
            }}
            className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center transition-colors"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        )}
        <span>
          {appType === "trucking" && showFileUpload && fileUploadStep === "form"
            ? terms.createJobFromFileTitle
            : terms.createJobTitle}
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      visible={modalOpen}
      setVisible={setModalOpen}
      onClose={closeModal}
      title={title}
      footer={footer}
      content={mainContent}
      footerPosition="right"
      contentWrapperclassName="p-0"
      size={showFileUpload && fileUploadStep === "form" ? "xl" : "lg"}
    />
  );
};

export default CreateModal;
